/**
 * SecondFirst — Page Transitions, Cart Animations & Quick-Add Toast
 *
 * 1. Page exit   : smooth fade-out before navigating to another page
 * 2. Cart bounce : header cart icon bounces when an item is added
 * 3. Modal close : sf-modal-out plays before Dawn clears the modal content
 * 4. Quick-add ✓ : "Added to cart" toast + auto-close modal on successful add
 */

// ══ 1. PAGE EXIT TRANSITION ══════════════════════════════════════════════════

(function () {
  'use strict';

  window.sfPageExit = function (href) {
    // Respect "prefers-reduced-motion": skip the fade + 260ms delay and
    // navigate immediately. Covers both link clicks and programmatic callers
    // (e.g. sf-quick-add.js).
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.location.href = href;
      return;
    }
    document.documentElement.classList.add('sf-page-exiting');
    setTimeout(function () { window.location.href = href; }, 260);
  };

  document.addEventListener('click', function (e) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest('a[href]');
    if (!link) return;

    var rawHref = link.getAttribute('href') || '';
    var href    = link.href;

    if (link.target === '_blank'
      || rawHref.startsWith('#')
      || rawHref.startsWith('javascript:')
      || rawHref.startsWith('mailto:')
      || rawHref.startsWith('tel:')
      || href.includes('/checkout')
    ) return;

    if (e.target.closest('.card__inner')) return;

    // Links that other scripts handle themselves. Facet chips re-render the
    // product grid in place; hijacking them fires the AJAX update AND a full
    // reload. role="button" is set by facets.js/cart-drawer.js on exactly the
    // links that act as controls rather than navigation.
    if (link.closest('facet-remove') || link.getAttribute('role') === 'button') return;

    try { if (new URL(href).origin !== window.location.origin) return; } catch (_) { return; }
    if (href.split('#')[0] === window.location.href.split('#')[0]) return;

    e.preventDefault();
    window.sfPageExit(href);
  }, true);

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) document.documentElement.classList.remove('sf-page-exiting');
  });
})();


// ══ 2. CART ICON BOUNCE ═══════════════════════════════════════════════════════

(function () {
  'use strict';

  function initCartBounce() {
    var bubble = document.querySelector('.cart-count-bubble');
    if (!bubble) return;

    var cartLink = bubble.closest('a, button');
    var prevText = bubble.textContent;

    new MutationObserver(function () {
      var newText = bubble.textContent;
      if (newText === prevText) return;
      prevText = newText;

      if (cartLink) {
        cartLink.classList.remove('sf-cart-bounce');
        void cartLink.offsetWidth;
        cartLink.classList.add('sf-cart-bounce');
        cartLink.addEventListener('animationend', function () {
          cartLink.classList.remove('sf-cart-bounce');
        }, { once: true });
      }
    }).observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartBounce);
  } else {
    initCartBounce();
  }
})();


// ══ 4. QUICK-ADD TOAST + AUTO-CLOSE ══════════════════════════════════════════
//
// Detection strategy (two parallel approaches — first one wins):
//
//  A) Watch the submit button's `loading` class.
//     Dawn's product-form.js adds `loading` on submit and removes it on finish.
//     When `loading` disappears and no error is shown → success.
//
//  B) Watch the cart-count bubble for any text change within 8 s of a button
//     click. Covers edge-cases where approach A's mutation never fires.

(function () {
  'use strict';

  // ── Shared state ───────────────────────────────────────────────────────────
  var pendingModal    = null;   // modal that triggered the add
  var pendingTime     = 0;      // timestamp of the button click
  var successShown    = false;  // guard against double-firing

  // ── Toast ──────────────────────────────────────────────────────────────────
  function showToastAndCloseModal(modal) {
    if (successShown) return;
    successShown = true;

    // Build toast — unless one is already on screen (the cart-notification flow
    // may have shown it). Dedupe so quick-add never stacks two toasts.
    if (!document.querySelector('.sf-cart-toast')) {
      var toast = document.createElement('div');
      toast.className = 'sf-cart-toast';
      toast.innerHTML =
        '<span class="sf-cart-toast__icon">' +
          '<svg viewBox="0 0 24 24"><polyline points="4 12 9 17 20 6"></polyline></svg>' +
        '</span>' +
        'Added to cart';
      document.body.appendChild(toast);

      // Fade out and remove the toast
      setTimeout(function () {
        toast.classList.add('sf-toast-exit');
        toast.addEventListener('animationend', function () {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, { once: true });
      }, 2200);
    }

    // Close the modal shortly after the toast appears
    setTimeout(function () {
      if (modal && typeof modal.hide === 'function') modal.hide();
    }, 500);

    // Reset state so next add works
    setTimeout(function () {
      successShown  = false;
      pendingModal  = null;
      pendingTime   = 0;
    }, 3000);
  }

  // ── Approach A: loading-class observer ────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.product-form__submit');
    if (!btn) return;

    var modal = document.querySelector('quick-add-modal[open]');
    if (!modal || !modal.contains(btn)) return;

    // Record for approach B as well
    pendingModal = modal;
    pendingTime  = Date.now();
    successShown = false;

    // Start observing one tick later (Dawn adds `loading` on the `submit` event,
    // which fires just after this click event)
    setTimeout(function () {
      var observer = new MutationObserver(function () {
        if (btn.classList.contains('loading')) return; // still in-flight
        observer.disconnect();

        setTimeout(function () {
          if (successShown) return;
          if (!modal.hasAttribute('open')) {
            // Modal already closed by Dawn — just show toast, skip close
            showToastAndCloseModal(null);
            return;
          }
          var errEl = modal.querySelector('.product-form__error-message-wrapper');
          var hasErr = errEl && !errEl.hasAttribute('hidden') && errEl.textContent.trim() !== '';
          if (!hasErr) showToastAndCloseModal(modal);
        }, 150);
      });

      observer.observe(btn, { attributes: true, attributeFilter: ['class'] });
      setTimeout(function () { observer.disconnect(); }, 12000); // safety timeout
    }, 0);
  }, true);

  // ── Approach B: cart-count fallback ───────────────────────────────────────
  function initCartCountFallback() {
    var bubble = document.querySelector('.cart-count-bubble');
    if (!bubble) return;

    var prevText = bubble.textContent;

    new MutationObserver(function () {
      var newText = bubble.textContent;
      if (newText === prevText) return;
      prevText = newText;

      // Only trigger if we have a pending modal add within the last 8 s
      if (!pendingModal || successShown) return;
      if (Date.now() - pendingTime > 8000) return;

      var modal = pendingModal;
      if (modal.hasAttribute('open')) {
        showToastAndCloseModal(modal);
      }
    }).observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartCountFallback);
  } else {
    initCartCountFallback();
  }
})();


// ══ 3. QUICK-ADD MODAL — CLOSE ANIMATION ═════════════════════════════════════

(function () {
  'use strict';

  function patchModalHide(modal) {
    if (modal.__sfAnimPatched) return;
    modal.__sfAnimPatched = true;

    var originalHide = modal.hide.bind(modal);

    modal.hide = function (preventFocus) {
      if (modal.__sfClosing) return;

      var content    = modal.querySelector('.quick-add-modal__content');
      var infoId     = 'QuickAddInfo-' + modal.id.replace('QuickAdd-', '');
      var info       = modal.querySelector('#' + infoId);
      var hasContent = info && info.innerHTML.trim() !== '';

      if (content && hasContent) {
        modal.__sfClosing = true;
        content.classList.add('sf-modal-closing');

        var called = false;
        var finish = function () {
          if (called) return;
          called = true;
          content.classList.remove('sf-modal-closing');
          modal.__sfClosing = false;
          originalHide(preventFocus);
        };

        var timeout = setTimeout(finish, 350);

        var onAnimEnd = function (e) {
          if (e.animationName !== 'sf-modal-out') return;
          content.removeEventListener('animationend', onAnimEnd);
          clearTimeout(timeout);
          finish();
        };
        content.addEventListener('animationend', onAnimEnd);
        return;
      }

      originalHide(preventFocus);
    };
  }

  function patchAll() {
    document.querySelectorAll('quick-add-modal').forEach(patchModalHide);
  }

  if (typeof customElements !== 'undefined' && customElements.whenDefined) {
    customElements.whenDefined('quick-add-modal').then(function () {
      patchAll();

      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType === 1
                && node.tagName
                && node.tagName.toLowerCase() === 'quick-add-modal') {
              patchModalHide(node);
            }
          });
        });
      }).observe(document.body, { childList: true });
    });
  }
})();

// ══ 5. INSTAFEED FONT OVERRIDE (pierces Shadow DOM) ══════════════════════════
(function () {
  var FONT = '"Space Grotesk", sans-serif';
  var SECTION_ID = 'shopify-section-1775502882cd1ef49f';

  function applyFont(root) {
    if (!root) return;
    root.querySelectorAll('*').forEach(function (el) {
      // Force font on any non-image text element
      var tag = el.tagName ? el.tagName.toLowerCase() : '';
      if (tag && tag !== 'img' && tag !== 'svg' && tag !== 'path' && tag !== 'use') {
        el.style.setProperty('font-family', FONT, 'important');
      }
      // Recurse into shadow roots
      if (el.shadowRoot) applyFont(el.shadowRoot);
    });
  }

  function run() {
    var section = document.getElementById(SECTION_ID);
    if (section) applyFont(section);
  }

  // Run on load and after short delays for dynamic content
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      run();
      setTimeout(run, 800);
      setTimeout(run, 2500);
    });
  } else {
    run();
    setTimeout(run, 800);
    setTimeout(run, 2500);
  }
})();
